import React, { useState } from "react";
import { Share2, Copy, Twitter, Instagram, Music2 } from "lucide-react";
import { toast } from "sonner";

/**
 * One-tap shareable product post for IG / X / TikTok.
 * Mirrors ShareCampaign but tuned for a storefront PDP.
 */
export default function ShareProduct({ product }) {
  const [open, setOpen] = useState(false);
  if (!product) return null;
  const url = typeof window !== "undefined" ? `${window.location.origin}/shop/product/${product.id}` : "";
  const priceLine = `$${Number(product.price_usd || 0).toFixed(2)}`;
  const byLine = product.creator_name ? ` by ${product.creator_name}` : "";
  const text = `${product.title}${byLine} — ${priceLine}\n\nGrab it on Tap In TV → ${url}\n\n#TapInTV #IndieCreators #ShopTheDrop`;

  const copy = async (payload) => {
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy blocked — long-press to copy manually");
    }
  };

  const openX = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(twitterUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button
        type="button"
        data-testid="share-product-btn"
        onClick={() => setOpen(true)}
        className="tv-glass border border-white/15 hover:border-tv-accent/50 text-white font-semibold px-4 py-3 rounded-full flex items-center gap-2 text-sm"
        aria-label="Share this product"
      >
        <Share2 className="w-4 h-4" /> Share this product
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div data-testid="share-product-modal" className="tv-glass rounded-2xl w-full max-w-lg border border-white/10 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10">
              <div className="text-xs uppercase tracking-widest text-tv-accent font-bold">Share this product</div>
              <div className="font-display font-bold text-white text-xl mt-1 line-clamp-1">{product.title}</div>
            </div>

            <div className="p-5 space-y-4">
              <div className="tv-glass rounded-lg border border-white/10 p-3">
                <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-1">Pre-filled post</div>
                <pre data-testid="share-product-text" className="whitespace-pre-wrap text-white/90 text-sm leading-relaxed font-sans">{text}</pre>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  data-testid="share-product-twitter"
                  onClick={openX}
                  className="flex items-center justify-center gap-2 bg-black text-white border border-white/20 hover:bg-white/5 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Twitter className="w-4 h-4" /> Post on X
                </button>
                <button
                  data-testid="share-product-instagram"
                  onClick={() => copy(text)}
                  className="flex items-center justify-center gap-2 bg-gradient-to-tr from-pink-500 to-yellow-400 text-black hover:brightness-110 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Instagram className="w-4 h-4" /> Copy for IG
                </button>
                <button
                  data-testid="share-product-tiktok"
                  onClick={() => copy(text)}
                  className="flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Music2 className="w-4 h-4" /> Copy for TikTok
                </button>
                <button
                  data-testid="share-product-copy-link"
                  onClick={() => copy(url)}
                  className="flex items-center justify-center gap-2 tv-glass text-white border border-white/20 hover:bg-white/5 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Copy className="w-4 h-4" /> Copy link
                </button>
              </div>

              <div className="text-xs text-white/50 text-center">
                Creators who share daily convert <span className="text-tv-brand font-semibold">3× more sales</span> on drop days.
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end">
              <button onClick={() => setOpen(false)} className="text-white/70 px-4 py-2" data-testid="share-product-close">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
